//frontend/src/services/api.js
import axios from 'axios';

// Axios instance үүсгэх
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5000/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request Interceptor - Token нэмэх
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response Interceptor - Алдаа шийдвэрлэх
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token буруу эсвэл хугацаа дууссан
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ============================================
// AUTH API
// ============================================

export const authAPI = {
  smartLogin: (credentials) => api.post('/auth/smart-login', credentials),
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  me: () => api.get('/auth/me'),
  
//=========== Password recovery ================
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  verifyResetCode: (email, code) => api.post('/auth/verify-reset-code', { email, code }),
  resetPassword: (email, code, new_password) => api.post('/auth/reset-password', { email, code, new_password }),
};


// ============================================
// COMPANY API
// ============================================

export const companyAPI = {
  getAll: (params) => api.get('/companies', { params }),
   getById: (id, params = {}) => api.get(`/companies/${id}`, { params }),
  create: (formData) => api.post('/companies', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, formData) => api.put(`/companies/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateStatus: (id, status) => api.patch(`/companies/${id}/status`, { status }),
  resetPassword: (id, adminId) => api.post(`/companies/${id}/reset-password`, { admin_id: adminId }), 
  delete: (id) => api.delete(`/companies/${id}`),
  
  // Notification settings
  getNotificationSettings: (id) => api.get(`/companies/${id}/notification-settings`),
  updateNotificationSettings: (id, data) => api.put(`/companies/${id}/notification-settings`, data),
  
  // ==========================================
  // ШИНЭ: Company settings (name, logo) - sidebar display
  // ==========================================
  updateCompanySettings: (id, formData) => api.put(`/companies/${id}/settings`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  // ==========================================
  // ШИНЭ: User profile (for company users)
  // ==========================================
  updateProfile: (formData) => api.put('/company/profile', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  // ==========================================
  // ШИНЭ: Change password
  // ==========================================
  changePassword: (data) => api.put('/company/change-password', data),
};

// ============================================
// WAREHOUSE API
// ============================================

export const warehouseAPI = {
  getAll: (params) => api.get('/warehouses', { params }),
  getById: (id, params = {}) => api.get(`/warehouses/${id}`, { params }),
  create: (data) => api.post('/warehouses', data),
  update: (id, data) => api.put(`/warehouses/${id}`, data),
  toggleStatus: (id, status) => api.patch(`/warehouses/${id}/status`, { status }),
  delete: (id) => api.delete(`/warehouses/${id}`),
};


// ============================================
// DISTRICT API
// ============================================

export const districtAPI = {
  getAll: () => api.get('/districts'),
  
  // Тухайн дүүргийн хороод
  getKhoroosByDistrict: (districtId) => api.get(`/districts/${districtId}/khoroos`),
  
  // Бүх хороод (admin)
  getAllKhoroos: () => api.get('/districts/khoroos/all'),
};

// ============================================
// SHOP API
// ============================================

export const shopAPI = {
  getAll: (params) => api.get('/shops', { params }),
  getById: (id) => api.get(`/shops/${id}`),
  create: (data) => api.post('/shops', data),
  update: (id, data) => api.put(`/shops/${id}`, data),
  toggleStatus: (id, status) => api.patch(`/shops/${id}/status`, { status }),
  delete: (id) => api.delete(`/shops/${id}`),
  resetPassword: (id) => api.post(`/shops/${id}/reset-password`), 
};

// ============================================
// CATEGORY API
// ============================================

export const categoryAPI = {
  getAll: (params) => api.get('/categories', { params }),
  getTree: () => api.get('/categories/tree'),
  getById: (id) => api.get(`/categories/${id}`),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
};

// ============================================
// PRODUCT API
// ============================================

export const productAPI = {
  getAll: (params) => api.get('/products', { params }),
  getById: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  
  // === VARIANTS ===
  createVariant: (productId, data) => api.post(`/products/${productId}/variants`, data),
  updateVariant: (productId, variantId, data) => api.put(`/products/${productId}/variants/${variantId}`, data),
  deleteVariant: (productId, variantId) => api.delete(`/products/${productId}/variants/${variantId}`),
};

// ============================================
// INVENTORY API
// ============================================

export const inventoryAPI = {
  // Create receipt (бараа орлого - худалдан авалт)
  createReceipt: (data) => api.post('/inventory/receipts', data),
  
  // Create return receipt (буцаалт бүртгэх)
  createReturnReceipt: (data) => api.post('/inventory/receipts/return', data),
  
  // Get all receipts
  getAllReceipts: (params) => api.get('/inventory/receipts', { params }),
  
  // Get receipt by ID
  getReceiptById: (id) => api.get(`/inventory/receipts/${id}`),
  
  // Update receipt
  updateReceipt: (id, data) => api.put(`/inventory/receipts/${id}`, data),
  
  // Delete receipt
  deleteReceipt: (id) => api.delete(`/inventory/receipts/${id}`),
  
  // Get stock levels (үлдэгдэл)
  getStock: (params) => api.get('/inventory/stock', { params }),
  
  // Get transaction history
  getTransactions: (params) => api.get('/inventory/transactions', { params }),
};

// ============================================
// OUTBOUND API
// ============================================

export const outboundAPI = {
  createOutbound: (data) => api.post('/outbound/outbounds', data),
  getAllOutbounds: (params) => api.get('/outbound/outbounds', { params }),
  getOutboundById: (id) => api.get(`/outbound/outbounds/${id}`),
  updateOutbound: (id, data) => api.put(`/outbound/outbounds/${id}`, data), 
  deleteOutbound: (id) => api.delete(`/outbound/outbounds/${id}`),           
};

// ============================================
// DELIVERY API (COMPANY ADMIN)
// ============================================

export const deliveryAPI = {
  create: (data) => api.post('/deliveries/create', data),
  getAll: (params) => api.get('/deliveries', { params }),
  getById: (id) => api.get(`/deliveries/${id}`),
  updateStatus: (id, data) => api.patch(`/deliveries/${id}/status`, data),
  assignDriver: (id, driverId) => api.patch(`/deliveries/${id}/assign`, { driver_id: driverId }),
  bulkAssign: (data) => api.post('/deliveries/bulk-assign', data),
  getStats: (params) => api.get('/deliveries/stats', { params }),
  update: (id, data) => api.put(`/deliveries/${id}`, data)
};

// ============================================
// DRIVER API
// ============================================

export const driverAPI = {
  // Get all drivers with filters & stats
  getAll: (params) => api.get('/drivers', { params }),
  
  // Get driver by ID with details
  getById: (id) => api.get(`/drivers/${id}`),
  
  // Get driver statistics
  getStats: (id, params) => api.get(`/drivers/${id}/stats`, { params }),
  
  // Create new driver
  create: (data) => api.post('/drivers', data),
  
  // Update driver
  update: (id, data) => api.put(`/drivers/${id}`, data),
  
  // Update driver status
  updateStatus: (id, status) => api.patch(`/drivers/${id}/status`, { status }),
  
  // Delete driver (soft delete)
  delete: (id) => api.delete(`/drivers/${id}`)
};

// ============================================
// ZONE API
// ============================================

export const zoneAPI = {
  getAll: (params) => api.get('/zones', { params }),
  getById: (id) => api.get(`/zones/${id}`),
  create: (data) => api.post('/zones', data),
  update: (id, data) => api.put(`/zones/${id}`, data),
  delete: (id) => api.delete(`/zones/${id}`),
  
  // Zone-District mapping
  assignDistricts: (zoneId, districtIds) => api.post(`/zones/${zoneId}/districts`, { district_ids: districtIds }),
  removeDistrict: (zoneId, districtId) => api.delete(`/zones/${zoneId}/districts/${districtId}`)
};

// ============================================
// SHOP ADMIN API
// ============================================

export const shopAdminAPI = {
  // Dashboard stats
  getStats: () => api.get('/shop/stats'),
  
  // Deliveries
  createDelivery: (data) => api.post('/shop/deliveries', data),
  getDeliveries: (params) => api.get('/shop/deliveries', { params }),
  getDeliveryById: (id) => api.get(`/shop/deliveries/${id}`),
  updateDelivery: (id, data) => api.put(`/shop/deliveries/${id}`, data),
  deleteDelivery: (id) => api.delete(`/shop/deliveries/${id}`),
  
  // Categories (Ангилал)
  getCategories: () => api.get('/shop/categories'),
  
  // Products (Бараа)
  getProducts: (params) => api.get('/shop/products', { params }),
  getProductById: (id) => api.get(`/shop/products/${id}`),
  createProduct: (data) => api.post('/shop/products', data),
  updateProduct: (id, data) => api.put(`/shop/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/shop/products/${id}`),
  
  // Variants (Сонголт)
  createProductVariant: (productId, data) => api.post(`/shop/products/${productId}/variants`, data),
  updateProductVariant: (productId, variantId, data) => api.put(`/shop/products/${productId}/variants/${variantId}`, data),
  deleteProductVariant: (productId, variantId) => api.delete(`/shop/products/${productId}/variants/${variantId}`),
};

// ============================================
// ADMIN API (SYSTEM ADMIN)
// ============================================

export const adminAPI = {
  // Dashboard stats
  getDashboardStats: () => api.get('/admin/dashboard/stats'),
  
  // Google Maps
  getGoogleMapsUsage: () => api.get('/admin/google-maps/usage'),
  updateGoogleMapsSettings: (data) => api.put('/admin/google-maps/settings', data),
  resetGoogleMapsUsage: () => api.post('/admin/google-maps/reset'),
  
  // System info
  getSystemInfo: () => api.get('/admin/system/info')
};

// ============================================
// SYSTEM BANK ACCOUNT API (СИСТЕМИЙН ДАНС) - ШИНЭ!
// ============================================

export const systemBankAPI = {
  // Бүх данс авах
  getAll: () => api.get('/system/bank-accounts'),
  
  // Идэвхтэй данснууд (dropdown-д)
  getActive: () => api.get('/system/bank-accounts/active'),
  
  // Нэг данс авах
  getById: (id) => api.get(`/system/bank-accounts/${id}`),
  
  // Шинэ данс үүсгэх
  create: (data) => api.post('/system/bank-accounts', data),
  
  // Данс засах
  update: (id, data) => api.put(`/system/bank-accounts/${id}`, data),
  
  // Данс устгах
  delete: (id) => api.delete(`/system/bank-accounts/${id}`)
};

// ============================================
// SETTLEMENT API (ТӨЛБӨР ТООЦОО) - ШИНЭ!
// ============================================

export const settlementAPI = {
  // ============================================
  // КОМПАНИЙН АДМИН - ШОПУУДЫН ТООЦОО
  // ============================================
  
  // Бүх шопуудын төлбөр тооцоо
  getShopSettlements: (params) => api.get('/settlements/company/shops', { params }),
  
  // Нэг шопын дэлгэрэнгүй тооцоо
  getShopSettlementDetail: (shopId, params) => api.get(`/settlements/company/shops/${shopId}`, { params }),
  
  // Шопоос төлбөр хүлээн авах
  recordShopPayment: (shopId, data) => api.post(`/settlements/company/shops/${shopId}/payment`, data),
  
  // Өдрийн хүргэлтийн төлбөр тооцоо үүсгэх
  generateDailyShopFees: (date) => api.post('/settlements/company/generate-daily-fees', { date }),
  
  // Компанийн системийн шимтгэл харах
  getCompanyCommission: (params = {}) => api.get('/settlements/company/commission', { params }),
  
  // ============================================
  // СИСТЕМИЙН АДМИН - КОМПАНИУДЫН ТООЦОО
  // ============================================
  
  // Бүх компанийн шимтгэл тооцоо
  getAllCompanySettlements: (params) => api.get('/settlements/admin/companies', { params }),
  
  // Нэг компанийн дэлгэрэнгүй тооцоо
  getCompanySettlementDetail: (companyId, params) => api.get(`/settlements/admin/companies/${companyId}`, { params }),
  
  // Компаниас төлбөр хүлээн авах
   recordCompanyPayment: (companyId, data) => api.post(`/settlements/admin/companies/${companyId}/payment`, data),
  
  // Өдрийн шимтгэл тооцоо үүсгэх
  generateDailyCommissions: (date) => api.post('/settlements/admin/generate-daily-commissions', { date }),
  
  // ============================================
  // ШОПЫН АДМИН - ӨӨРИЙН ТООЦОО
  // ============================================
  
  // Шопын өөрийн төлбөр тооцоо
  getMyShopSettlement: (params = {}) => api.get('/settlements/shop/my-settlement', { params })
};

// ============================================
// COD SETTLEMENT API (COD ТООЦОО) - ШИНЭ!
// ============================================

export const codSettlementAPI = {
  // ============================================
  // КОМПАНИЙН АДМИН - COD ТООЦОО
  // ============================================
  
  // COD тооцоо татах (Компани → Шоп)
  getCodSettlements: (params) => api.get('/company/cod-settlements', { params }),
  
  // COD төлбөр бүртгэх
  recordCodPayment: (data) => api.post('/company/cod-settlements/pay', data),
  
  // Жолоочийн COD тооцоо татах (Жолооч → Компани)
  getDriverCodSettlements: (params) => api.get('/company/driver-cod-settlements', { params }),
  
  // Жолоочийн шилжүүлэг бүртгэх
  recordDriverTransfer: (data) => api.post('/company/driver-cod-settlements/transfer', data),
  
  // Төлбөрийн түүх
  getCodPaymentHistory: (params) => api.get('/company/cod-payment-history', { params }),
  
  // ============================================
  // ШОПЫН АДМИН - COD ТООЦОО
  // ============================================
  
  // ============================================
  // ШОПЫН АДМИН - COD ТООЦОО
  // ============================================
  
  // Шопын COD тооцоо татах
  getShopCodSettlements: (params) => api.get('/shop/cod-settlements', { params }),
  
  // ============================================
  // COD PAYMENT - ШИНЭ СИСТЕМ
  // ============================================
  
  // Компани - COD төлбөр бүртгэх (pending төлөвтэй)
  createCodPayment: (data) => api.post('/companies/cod-payments', data),
  
  // Компани - Төлбөрийн түүх
  getCodPayments: (params) => api.get('/companies/cod-payments', { params }),
  
  // Шоп - Төлбөрийн түүх
  getShopCodPayments: (params) => api.get('/shops/cod-payments', { params }),
  
  // Шоп - Хүлээгдэж буй төлбөрүүд
  getShopPendingPayments: () => api.get('/shops/cod-payments/pending'),
  
  // Шоп - Төлбөр баталгаажуулах
  confirmCodPayment: (id, data) => api.put(`/shops/cod-payments/${id}/confirm`, data),
  
  // Шоп - Төлбөр татгалзах
  rejectCodPayment: (id, data) => api.put(`/shops/cod-payments/${id}/reject`, data),
};

// ============================================
// COMPANY BANK ACCOUNT API (КОМПАНИЙН ДАНС)
// ============================================

export const companyBankAccountAPI = {
  // Бүх данс авах
  getAll: () => api.get('/company/bank-accounts'),
  
  // Нэг данс авах
  getById: (id) => api.get(`/company/bank-accounts/${id}`),
  
  // Шинэ данс үүсгэх
  create: (data) => api.post('/company/bank-accounts', data),
  
  // Данс засах
  update: (id, data) => api.put(`/company/bank-accounts/${id}`, data),
  
  // Данс устгах
  delete: (id) => api.delete(`/company/bank-accounts/${id}`),
  
  // Үндсэн данс болгох
  setDefault: (id) => api.put(`/company/bank-accounts/${id}/default`)
};

// ============================================
// SHOP BANK ACCOUNT API (ШОПЫН ДАНС)
// ============================================

export const shopBankAccountAPI = {
  // Бүх данс авах
  getAll: () => api.get('/shop/bank-accounts'),
  
  // Шинэ данс үүсгэх
  create: (data) => api.post('/shop/bank-accounts', data),
  
  // Данс засах
  update: (id, data) => api.put(`/shop/bank-accounts/${id}`, data),
  
  // Данс устгах
  delete: (id) => api.delete(`/shop/bank-accounts/${id}`),
  
  // Үндсэн данс болгох
  setDefault: (id) => api.put(`/shop/bank-accounts/${id}/default`)
};

// ============================================
// SYSTEM USER API (СИСТЕМИЙН ХЭРЭГЛЭГЧ) - ШИНЭ!
// ============================================

export const systemUserAPI = {
  // Бүх хэрэглэгч авах
  getAll: () => api.get('/system/users'),
  
  // Нэг хэрэглэгч авах
  getById: (id) => api.get(`/system/users/${id}`),
  
  // Шинэ хэрэглэгч үүсгэх
  create: (data) => api.post('/system/users', data),
  
  // Хэрэглэгч засах
  update: (id, data) => api.put(`/system/users/${id}`, data),
  
  // Хэрэглэгч устгах
  delete: (id) => api.delete(`/system/users/${id}`),
  
  // Эрхүүдийн жагсаалт
  getPermissions: () => api.get('/system/permissions')
};

// ============================================
// LOGIN SETTINGS API (LOGIN ХУУДАСНЫ ТОХИРГОО)
// ============================================

export const loginSettingsAPI = {
  // ============================================
  // PUBLIC APIs (Login хуудсанд)
  // ============================================
  
  // Бүх тохиргоо (logo + slides + config)
  getLoginSettings: async () => {
    const response = await api.get('/login-settings');
    return response.data;
  },
  
  // Идэвхтэй слайдууд
  getActiveSlides: async () => {
    const response = await api.get('/login-settings/slides');
    return response.data;
  },
  
  // Лого URL
  getLogo: async () => {
    const response = await api.get('/login-settings/logo');
    return response.data;
  },
  
  // Slider тохиргоо
  getSliderConfig: async () => {
    const response = await api.get('/login-settings/slider-config');
    return response.data;
  },

  // ============================================
  // ADMIN APIs (System Admin)
  // ============================================
  
  // Бүх слайд (идэвхгүй ч)
  getAllSlides: async () => {
    const response = await api.get('/login-settings/slides/all');
    return response.data;
  },
  
  // Шинэ слайд үүсгэх
  createSlide: async (data) => {
    const response = await api.post('/login-settings/slides', data);
    return response.data;
  },
  
  // Слайд засах
  updateSlide: async (id, data) => {
    const response = await api.put(`/login-settings/slides/${id}`, data);
    return response.data;
  },
  
  // Слайд устгах
  deleteSlide: async (id) => {
    const response = await api.delete(`/login-settings/slides/${id}`);
    return response.data;
  },
  
  // Слайдын дараалал өөрчлөх
  reorderSlides: async (slideIds) => {
    const response = await api.put('/login-settings/slides/reorder', { slideIds });
    return response.data;
  },
  
  // Лого upload
  uploadLogo: async (file) => {
    const formData = new FormData();
    formData.append('logo', file);
    const response = await api.post('/login-settings/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
  
  // Slider тохиргоо хадгалах
  updateSliderConfig: async (config) => {
    const response = await api.put('/login-settings/slider-config', config);
    return response.data;
  }
};

// ============================================
// PUBLIC API (Нэвтрэлгүйгээр хандах) - ШИНЭ!
// ============================================

export const publicAPI = {
  // Компани бүртгүүлэх хүсэлт илгээх
  registerCompany: async (formData) => {
    const response = await api.post('/public/register-company', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }
};

// ============================================
// NOTIFICATION API (Мэдэгдэл)
// ============================================

export const notificationAPI = {
  // Миний мэдэгдлүүд
  getMyNotifications: (params) => api.get('/notifications', { params }),
  getNotifications: (params) => api.get('/notifications', { params }),  // Alias
  
  // Уншаагүй тоо
  getUnreadCount: () => api.get('/notifications/unread-count'),
  
  // Уншсан болгох
  markAsRead: (id) => api.patch(`/notifications/${id}/read`),
  
  // Бүгдийг уншсан болгох
  markAllAsRead: () => api.patch('/notifications/read-all'),
  
  // Устгах
  deleteNotification: (id) => api.delete(`/notifications/${id}`),
  
  // === System Admin Only ===
  
  // Мэдэгдэл илгээх
  sendNotification: (data) => api.post('/notifications/send', data),
  
  // Компани бүртгэлийн хүсэлтүүд
  getCompanyRegistrations: (params) => api.get('/notifications/company-registrations', { params }),
  
  // Бүртгэл дэлгэрэнгүй
  getCompanyRegistrationById: (id) => api.get(`/notifications/company-registrations/${id}`),
  
  // Бүртгэл зөвшөөрөх
  approveRegistration: (id, data) => api.post(`/notifications/company-registrations/${id}/approve`, data),
  
  // Бүртгэл татгалзах
  rejectRegistration: (id, data) => api.post(`/notifications/company-registrations/${id}/reject`, data),

  // Бүртгэл устгах
deleteRegistration: (id) => api.delete(`/notifications/company-registrations/${id}`),

// Компанийн шимтгэл тооцоолох
getCompanyFee: (companyId, periodStart, periodEnd) => api.get(`/notifications/company-fee/${companyId}`, { 
  params: { period_start: periodStart, period_end: periodEnd } 
}),

// Invoice илгээх
sendInvoice: (data) => api.post('/notifications/send-invoice', data),

// Invoice түүх
getInvoiceHistory: (params) => api.get('/notifications/invoice-history', { params }),

// Invoice түүх устгах
deleteInvoiceHistory: (id) => api.delete(`/notifications/invoice-history/${id}`),

// Хуваарьт мэдэгдлүүд
getSchedules: (params) => api.get('/notifications/schedules', { params }),
cancelSchedule: (id) => api.delete(`/notifications/schedules/${id}`),

};


export default api;
